import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import {
  dateLevelHasSpotsFor,
  dateLevelMaxFor,
  hasSlotLevelAnswer
} from 'customer-flows/availability/tickets'

export default class ActivityTicketsStoreClass extends BaseStore {
  @context('availability:validTimeslots') validTimeslots: AvailabilityStore['validTimeslots']
  @context('availability:isAvailableStatus') isAvailableStatus: AvailabilityStore['isAvailableStatus']
  @context('availability:availabilityDates') availabilityDates: AvailabilityStore['availabilityDates']
  @context('availability:selectedDate') selectedDate: AvailabilityStore['selectedDate']
  @context('availability:timesLoading') timesLoading: AvailabilityStore['timesLoading']

  private get spotsMax(): number {
    return this.validTimeslots.reduce((acc, slot) => Math.min(acc, slot.spots ?? Infinity), Infinity)
  }

  // Which tier can answer — see `availability/tickets.ts` for the ladder.
  private get slotLevel(): boolean {
    return hasSlotLevelAnswer({ selectedDate: this.selectedDate, timesLoading: this.timesLoading })
  }

  isTicketAvailable(ticketId: string): boolean {
    if (!this.slotLevel) return dateLevelHasSpotsFor(this.availabilityDates.value, ticketId)

    return this.validTimeslots.some((slot) => this.isAvailableStatus(slot.status))
  }

  /**
   * Activities draw every ticket type from ONE shared pool, so the per-type capacity is additionally
   * capped by the slot's total `spots`. The date-level answer has no way to apply that cap — this is
   * the confirmed difference between the two tiers, not merely a coarser number: a slot with
   * `spots: 4` and a type showing 8 available yields `min(4, 8) = 4` once times land, against `8`
   * from the date-level answer. The old schema-presence gate made the date-level branch win on every
   * normal response, so that cap was effectively never applied.
   */
  getMaxAvailableQtyForTicket(ticketId: string): number {
    if (!this.slotLevel) return dateLevelMaxFor(this.availabilityDates.value, ticketId)

    return this.getMaxFromSlots(ticketId)
  }

  private getMaxFromSlots(ticketId: string): number {
    let max = 0
    let availabilityMax = 0

    this.validTimeslots.forEach((slot) => {
      const ticketAvailability = slot.availability.find(({ resourceOptionId }) => resourceOptionId === ticketId)
      if (ticketAvailability) {
        availabilityMax = Math.max(availabilityMax, ticketAvailability.qty)
      }
      slot.availability.forEach((a) => {
        max = Math.max(max, a.qty)
      })
    })

    const capacityMax = Math.max(max, availabilityMax)
    return Math.min(this.spotsMax, capacityMax)
  }
}

declare global {
  export type ActivityTicketsStore = ActivityTicketsStoreClass
  export interface StoreRegistry {
    'tickets/activity': ActivityTicketsStore
  }
}
